import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Drawer from "react-modern-drawer";
import { AsyncPaginate } from "react-select-async-paginate";

import {
    fetchPaymentCycles,
    patchFinanceOpsJob,
    postFinanceOpsJob,
    triggerPaymentRun,
} from "../../utils/apiUtils";
import { loadAccountHccIdentificationNumber } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import "./PaymentCycles.scss";

const GUIDE_DRAWER_SIZE = 440;
const JOB_TYPE_PAYMENT_RUN = "payment-run";

const isYes = (v) => {
    const s = String(v || "").toUpperCase();
    return s === "Y" || s === "YES" || s === "TRUE";
};

const DAY_NAMES = [
    "",
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

const money = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return Number(n).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    });
};

const pad2 = (n) => String(Number(n) || 0).padStart(2, "0");

const scheduleLabel = (row) => {
    const sched = (row.schedule || "").toUpperCase();
    const time =
        row.hourOfDay != null || row.minuteOfHour != null
            ? ` · ${pad2(row.hourOfDay)}:${pad2(row.minuteOfHour)}`
            : "";
    if (sched === "WEEKLY") {
        const day = DAY_NAMES[Number(row.dayOfWeek)] || `day ${row.dayOfWeek ?? "?"}`;
        return `Weekly · ${day}${time}`;
    }
    if (sched === "DAILY") return `Daily${time}`;
    if (sched === "MONTHLY") {
        if (row.runDayOfMonth != null) return `Monthly · day ${row.runDayOfMonth}${time}`;
        return `Monthly${time}`;
    }
    return `${row.schedule || "—"}${time}`;
};

const ynTone = (v) => {
    if (v == null || v === "" || v === "U") return { label: "—", tone: "" };
    const s = String(v).toUpperCase();
    if (s === "Y" || s === "YES" || s === "TRUE") return { label: "Yes", tone: "yes" };
    if (s === "N" || s === "NO" || s === "FALSE") return { label: "No", tone: "no" };
    return { label: String(v), tone: "" };
};

const YnValue = ({ value }) => {
    const { label, tone } = ynTone(value);
    if (!tone) return label;
    return (
        <span className={`payment-cycles__yn payment-cycles__yn--${tone}`}>{label}</span>
    );
};

const accountQueryParams = (account) => {
    const params = new URLSearchParams();
    if (account?.additional?.accountKey) {
        params.set("accountKey", String(account.additional.accountKey));
    }
    if (account?.additional?.accountHccId || account?.value) {
        params.set("accountHccId", account?.additional?.accountHccId || account.value);
    }
    if (account?.label || account?.additional?.accountName) {
        params.set(
            "accountName",
            account?.additional?.accountName || String(account.label || "").split(" · ")[0],
        );
    }
    return params;
};

const paymentsHref = (cycleName, account) => {
    const params = accountQueryParams(account);
    if (cycleName) params.set("paymentCycleKey", cycleName);
    const qs = params.toString();
    return `/finance/payments${qs ? `?${qs}` : ""}`;
};

const fundingHref = (account, cycleName) => {
    const params = accountQueryParams(account);
    if (cycleName) params.set("paymentCycleId", cycleName);
    const qs = params.toString();
    return `/finance/funding-requests${qs ? `?${qs}` : ""}`;
};

const PaymentCyclesGuide = () => (
    <div className="payment-cycles__panel-guide">
        <section aria-label="How money moves">
            <h3>How money moves</h3>
            <ol>
                <li>
                    <strong>Payable</strong> — Centivo owes a provider or vendor after a claim
                    (or supplier invoice).
                </li>
                <li>
                    <strong>Payment cycle</strong> — schedule + payee type (Supplier /
                    Subscriber) + payable type (Claims, etc.).
                </li>
                <li>
                    <strong>Payment run</strong> — executing the cycle (scheduled check run, or
                    HRP <code>triggerPaymentRun</code> / <code>submitPaymentRun</code>) issues
                    outbound payments and, for self-funded claim cycles, creates funding
                    requests. There is no separate “create funding request” API.
                </li>
                <li>
                    <strong>Funding request</strong> — employer cash call to cover claim
                    payables (inbound). Does not auto-withdraw.
                </li>
                <li>
                    <strong>Payment</strong> — issued check/EFT to the payee (outbound).
                </li>
            </ol>
            <p className="payment-cycles__guide-note">
                Premium invoices use <strong>billing cycles</strong> / BillRun (
                <code>triggerBillRun</code>), not payment cycles.
            </p>
        </section>

        <section aria-label="Payable types">
            <h3>Payable types</h3>
            <p className="payment-cycles__guide-intro">
                The <strong>payable type</strong> is what kind of money the cycle pays out.
                Paired with <strong>payee type</strong> (who receives it), it decides which
                open amounts the run picks up.
            </p>
            <dl className="payment-cycles__defs">
                <div>
                    <dt>Claims</dt>
                    <dd>
                        Adjudicated claim amounts owed to providers (Supplier) or member
                        reimbursements (Subscriber). Most common. Self-funded Supplier + Claims
                        runs also create funding requests.
                    </dd>
                </div>
                <div>
                    <dt>Capitated</dt>
                    <dd>
                        Fixed per-member (or similar) payments to a contracted supplier — not
                        tied to individual claim lines.
                    </dd>
                </div>
                <div>
                    <dt>Broker Commission</dt>
                    <dd>Commissions owed to brokers for sold/renewed coverage.</dd>
                </div>
                <div>
                    <dt>Lien Holder</dt>
                    <dd>
                        Amounts owed to a lien holder (e.g. subrogation / third-party interest)
                        instead of the usual payee.
                    </dd>
                </div>
                <div>
                    <dt>Premium Refund</dt>
                    <dd>
                        Premium dollars returned to the account or member — outbound refund, not
                        a claim payment.
                    </dd>
                </div>
            </dl>
        </section>

        <section aria-label="Payee types">
            <h3>Payee types</h3>
            <dl className="payment-cycles__defs payment-cycles__defs--compact">
                <div>
                    <dt>Supplier</dt>
                    <dd>Provider / vendor receives the payment (checks &amp; EFTs).</dd>
                </div>
                <div>
                    <dt>Subscriber</dt>
                    <dd>Member / subscriber receives reimbursements or refunds.</dd>
                </div>
                <div>
                    <dt>Broker</dt>
                    <dd>Broker receives commission payables.</dd>
                </div>
                <div>
                    <dt>Lien holder</dt>
                    <dd>Lien holder receives lien-related payables.</dd>
                </div>
            </dl>
        </section>
    </div>
);

const PaymentCycles = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const accountKeyParam = searchParams.get("accountKey");
    const accountHccIdParam = searchParams.get("accountHccId");
    const accountNameParam = searchParams.get("accountName");
    const [guideOpen, setGuideOpen] = useState(false);
    const [runResult, setRunResult] = useState(null);

    const [account, setAccount] = useState(() => {
        if (!accountKeyParam) return null;
        return {
            value: accountHccIdParam || accountKeyParam,
            label: accountNameParam
                ? `${accountNameParam}${accountHccIdParam ? ` · ${accountHccIdParam}` : ""}`
                : accountHccIdParam || accountKeyParam,
            additional: {
                accountKey: Number(accountKeyParam),
                accountHccId: accountHccIdParam || undefined,
                accountName: accountNameParam || undefined,
            },
        };
    });

    const accountKey = account?.additional?.accountKey;

    useEffect(() => {
        if (!accountKeyParam) return;
        setAccount((prev) => {
            if (prev?.additional?.accountKey === Number(accountKeyParam)) return prev;
            return {
                value: accountHccIdParam || accountKeyParam,
                label: accountNameParam
                    ? `${accountNameParam}${accountHccIdParam ? ` · ${accountHccIdParam}` : ""}`
                    : accountHccIdParam || accountKeyParam,
                additional: {
                    accountKey: Number(accountKeyParam),
                    accountHccId: accountHccIdParam || undefined,
                    accountName: accountNameParam || undefined,
                },
            };
        });
    }, [accountKeyParam, accountHccIdParam, accountNameParam]);

    const onAccountChange = (opt) => {
        setAccount(opt);
        const next = new URLSearchParams(searchParams);
        if (!opt?.additional?.accountKey) {
            next.delete("accountKey");
            next.delete("accountHccId");
            next.delete("accountName");
        } else {
            next.set("accountKey", String(opt.additional.accountKey));
            if (opt.additional.accountHccId || opt.value) {
                next.set("accountHccId", opt.additional.accountHccId || opt.value);
            }
            if (opt.additional.accountName || opt.label) {
                next.set(
                    "accountName",
                    opt.additional.accountName || String(opt.label || "").split(" · ")[0],
                );
            }
        }
        setSearchParams(next, { replace: true });
    };

    const { data, isLoading, error } = useQuery({
        queryKey: ["payment-cycles", accountKey || null],
        queryFn: () =>
            fetchPaymentCycles({
                accountId: accountKey,
                limit: 100,
                offset: 0,
            }),
        enabled: !!accountKey,
        staleTime: 30_000,
        retry: false,
    });

    const rows = useMemo(() => data?.rows || [], [data]);

    const paymentRunMutation = useMutation({
        mutationFn: async (row) => {
            const cycleId = row.paymentCycleName || row.description;
            if (!cycleId) throw new Error("Payment cycle name is required for triggerPaymentRun.");
            if (!accountKey) throw new Error("Select an account first.");

            const job = await postFinanceOpsJob({
                type: JOB_TYPE_PAYMENT_RUN,
                accountKey,
                accountHccId: account?.additional?.accountHccId || account?.value,
                accountName:
                    account?.additional?.accountName ||
                    String(account?.label || "").split(" · ")[0] ||
                    undefined,
                cycleId,
                meta: {
                    payeeType: row.payeeType || null,
                    payableType: row.payableType || null,
                    manualRun: row.manualRun || null,
                    paymentCycleFactKey: row.paymentCycleFactKey || null,
                },
            });

            await patchFinanceOpsJob(job.id, { status: "running", cycleId });
            try {
                const hrpResult = await triggerPaymentRun({ cycleId });
                const cvc = hrpResult?.cvcId ?? hrpResult?.result?.cvcId ?? null;
                const cvcId = Array.isArray(cvc) ? cvc[0] : cvc;
                const updated = await patchFinanceOpsJob(job.id, {
                    status: "succeeded",
                    hrpCorrelationId: cvcId != null ? String(cvcId) : null,
                    meta: { ...(job.meta || {}), hrpResult },
                });
                return { job: updated, cycleId, cvcId };
            } catch (err) {
                await patchFinanceOpsJob(job.id, {
                    status: "failed",
                    error: err?.message || "triggerPaymentRun failed",
                });
                throw err;
            }
        },
        onSuccess: (result) => {
            setRunResult({
                tone: "ok",
                cycleId: result.cycleId,
                message: result.cvcId
                    ? `Payment run started for “${result.cycleId}” (CVC ${result.cvcId}).`
                    : `Payment run accepted for “${result.cycleId}”.`,
            });
            queryClient.invalidateQueries({ queryKey: ["finance-ops-jobs"] });
            queryClient.invalidateQueries({ queryKey: ["payment-cycles", accountKey || null] });
            queryClient.invalidateQueries({ queryKey: ["funding-requests"] });
        },
        onError: (err, row) => {
            setRunResult({
                tone: "danger",
                cycleId: row?.paymentCycleName || null,
                message: err?.message || "Payment run failed.",
            });
            queryClient.invalidateQueries({ queryKey: ["finance-ops-jobs"] });
        },
    });

    const onRunPaymentCycle = (row) => {
        const cycleId = row.paymentCycleName || row.description || "this cycle";
        const claimsSupplier =
            String(row.payeeType || "").toLowerCase() === "supplier" &&
            String(row.payableType || "").toLowerCase() === "claims";

        const lines = [
            `Trigger HRP payment run for “${cycleId}”?`,
            "",
            `Payee: ${row.payeeType || "—"} · Payable: ${row.payableType || "—"}`,
        ];
        if (claimsSupplier) {
            lines.push(
                "",
                "Self-funded Supplier + Claims runs typically create funding requests.",
            );
        }
        lines.push("", "This calls triggerPaymentRun and is audited as a finance-ops job.");

        if (!window.confirm(lines.join("\n"))) return;
        setRunResult(null);
        paymentRunMutation.mutate(row);
    };

    const runningCycleName = paymentRunMutation.isPending
        ? paymentRunMutation.variables?.paymentCycleName ||
          paymentRunMutation.variables?.description ||
          null
        : null;

    return (
        <div className="container payment-cycles scrollable-form">
            <div className="payment-cycles__page-head">
                <div>
                    <p className="title">Payment Cycles</p>
                    <p className="sub-title">
                        Schedules that turn claim/vendor payables into provider checks or EFTs.
                        For self-funded accounts, a cycle run also drives funding requests —
                        separate from premium invoices.
                    </p>
                </div>
                <button
                    type="button"
                    className="payment-cycles__guide-toggle"
                    onClick={() => setGuideOpen((open) => !open)}
                    aria-pressed={guideOpen}
                    aria-expanded={guideOpen}
                >
                    {guideOpen ? "Hide guide" : "How payment cycles work"}
                </button>
            </div>

            {!!runResult && (
                <p
                    className={`payment-cycles__run-banner payment-cycles__run-banner--${runResult.tone}`}
                    role="status"
                >
                    {runResult.message}{" "}
                    {runResult.tone === "ok" && accountKey && (
                        <Link to={fundingHref(account)} className="payment-cycles__link">
                            View funding requests
                        </Link>
                    )}
                </p>
            )}

            <div className="payment-cycles__filters">
                <div className="payment-cycles__field payment-cycles__field--account">
                    <label>Account</label>
                    <AsyncPaginate
                        placeholder="Select an account"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        debounceTimeout={300}
                        value={account}
                        loadOptions={loadAccountHccIdentificationNumber}
                        onChange={onAccountChange}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{
                            ...customStyles,
                            menuPortal: (base) => ({ ...base, zIndex: 200 }),
                        }}
                        isClearable
                    />
                </div>
            </div>

            {!accountKey && (
                <p className="payment-cycles__muted">
                    Select an account to list its payment cycles.
                </p>
            )}

            {!!accountKey && isLoading && (
                <p className="payment-cycles__muted">Loading payment cycles…</p>
            )}
            {!!accountKey && error && (
                <p className="danger">{error.message || "Unable to load payment cycles."}</p>
            )}

            {!!accountKey && !isLoading && !error && (
                <>
                    <div className="payment-cycles__toolbar">
                        <p className="payment-cycles__count">
                            {rows.length} cycle{rows.length === 1 ? "" : "s"}
                        </p>
                        <div className="payment-cycles__toolbar-links">
                            <Link to={fundingHref(account)} className="payment-cycles__link">
                                Funding requests for this account
                            </Link>
                            <Link
                                to={paymentsHref(undefined, account)}
                                className="payment-cycles__link"
                            >
                                All provider payments
                            </Link>
                        </div>
                    </div>

                    {!rows.length && (
                        <p className="payment-cycles__muted">No payment cycles for this account.</p>
                    )}

                    <div className="payment-cycles__list">
                        {rows.map((row) => {
                            const name = row.paymentCycleName || row.description || "—";
                            const retired = String(row.isRetired || "").toUpperCase() === "Y";
                            const frCount = Number(row.fundingRequestCount) || 0;
                            const payee = String(row.payeeType || "").toLowerCase();
                            const payable = String(row.payableType || "").toLowerCase();
                            const isClaimsSupplier = payee === "supplier" && payable === "claims";
                            const showDesc =
                                row.description &&
                                row.description !== row.paymentCycleName;
                            const payeeBadge =
                                payee === "supplier"
                                    ? "payment-cycles__tag--supplier"
                                    : payee === "subscriber"
                                      ? "payment-cycles__tag--subscriber"
                                      : payee === "broker"
                                        ? "payment-cycles__tag--broker"
                                        : payee
                                          ? "payment-cycles__tag--payee"
                                          : "";
                            const payableBadge =
                                payable === "claims"
                                    ? "payment-cycles__tag--claims"
                                    : payable === "capitated"
                                      ? "payment-cycles__tag--capitated"
                                      : payable.includes("broker")
                                        ? "payment-cycles__tag--broker"
                                        : payable.includes("lien")
                                          ? "payment-cycles__tag--lien"
                                          : payable.includes("refund")
                                            ? "payment-cycles__tag--refund"
                                            : payable
                                              ? "payment-cycles__tag--payable"
                                              : "";
                            return (
                                <article
                                    key={row.paymentCycleFactKey || name}
                                    className={`payment-cycles__card${
                                        retired ? " payment-cycles__card--retired" : ""
                                    }`}
                                >
                                    <header className="payment-cycles__card-head">
                                        <div>
                                            <div className="payment-cycles__tags">
                                                {row.payeeType && (
                                                    <span
                                                        className={`payment-cycles__tag ${payeeBadge}`}
                                                    >
                                                        {row.payeeType}
                                                    </span>
                                                )}
                                                {row.payableType && (
                                                    <span
                                                        className={`payment-cycles__tag ${payableBadge}`}
                                                    >
                                                        {row.payableType}
                                                    </span>
                                                )}
                                                {frCount > 0 && (
                                                    <span className="payment-cycles__tag payment-cycles__tag--funding">
                                                        {frCount} funding
                                                    </span>
                                                )}
                                                {retired && (
                                                    <span className="payment-cycles__tag payment-cycles__tag--retired">
                                                        Retired
                                                    </span>
                                                )}
                                            </div>
                                            <h3>{name}</h3>
                                            {row.paymentCycleFactKey != null && (
                                                <p className="payment-cycles__muted">
                                                    Cycle #{row.paymentCycleFactKey}
                                                </p>
                                            )}
                                            {showDesc && (
                                                <p className="payment-cycles__muted">{row.description}</p>
                                            )}
                                        </div>
                                        <div className="payment-cycles__card-actions">
                                            {isYes(row.manualRun) && !retired && (
                                                <button
                                                    type="button"
                                                    className="btn payment-cycles__btn payment-cycles__btn--run"
                                                    disabled={
                                                        paymentRunMutation.isPending ||
                                                        !name ||
                                                        name === "—"
                                                    }
                                                    onClick={() => onRunPaymentCycle(row)}
                                                >
                                                    {runningCycleName === name
                                                        ? "Running…"
                                                        : "Run payment cycle"}
                                                </button>
                                            )}
                                            {frCount > 0 && (
                                                <button
                                                    type="button"
                                                    className="btn payment-cycles__btn payment-cycles__btn--funding"
                                                    onClick={() =>
                                                        navigate(fundingHref(account, name))
                                                    }
                                                >
                                                    Funding requests ({frCount})
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn payment-cycles__btn payment-cycles__btn--payments"
                                                onClick={() => navigate(paymentsHref(name, account))}
                                            >
                                                Provider payments
                                            </button>
                                        </div>
                                    </header>
                                    <dl className="payment-cycles__meta">
                                        <div>
                                            <dt>Schedule</dt>
                                            <dd>{scheduleLabel(row)}</dd>
                                        </div>
                                        <div>
                                            <dt>Manual run</dt>
                                            <dd><YnValue value={row.manualRun} /></dd>
                                        </div>
                                        <div>
                                            <dt>Payment review</dt>
                                            <dd><YnValue value={row.reviewRequired} /></dd>
                                        </div>
                                        <div>
                                            <dt>Funding review</dt>
                                            <dd><YnValue value={row.preApprovalRvwReqrdInd} /></dd>
                                        </div>
                                        <div>
                                            <dt>Auto-approve FR</dt>
                                            <dd><YnValue value={row.autoApproveFundingReqInd} /></dd>
                                        </div>
                                        <div>
                                            <dt>Credit funding immediately</dt>
                                            <dd><YnValue value={row.creditFundingImmediatelyInd} /></dd>
                                        </div>
                                        <div>
                                            <dt>Makeup for day off</dt>
                                            <dd>{row.makeUpForDayOff || "—"}</dd>
                                        </div>
                                        <div>
                                            <dt>Period start day</dt>
                                            <dd>
                                                {row.periodStartDayOfMonth != null
                                                    ? row.periodStartDayOfMonth
                                                    : "—"}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Run months ahead</dt>
                                            <dd>
                                                {row.runMonthsAhead != null
                                                    ? row.runMonthsAhead
                                                    : "—"}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Parent cycle</dt>
                                            <dd>
                                                {String(row.isParentPaymntCycleInd || "")
                                                    .toUpperCase() === "Y"
                                                    ? "Yes (parent)"
                                                    : row.parentCycleCycleId || "—"}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Funding requests</dt>
                                            <dd>
                                                {frCount > 0 ? (
                                                    <Link
                                                        to={fundingHref(account, name)}
                                                        className="payment-cycles__link"
                                                    >
                                                        {frCount} · {money(row.fundingRequestDue)} due
                                                    </Link>
                                                ) : (
                                                    "None"
                                                )}
                                            </dd>
                                        </div>
                                    </dl>
                                    <p className="payment-cycles__hint">
                                        {isClaimsSupplier
                                            ? "Claims → providers. Running this cycle typically creates funding requests (cash calls) for the employer."
                                            : payee === "subscriber"
                                              ? "Member/subscriber payables (e.g. reimbursements), not provider checks."
                                              : "Outbound payment schedule for this payee / payable type."}
                                    </p>
                                </article>
                            );
                        })}
                    </div>
                </>
            )}

            <Drawer
                open={guideOpen}
                onClose={() => setGuideOpen(false)}
                direction="right"
                size={GUIDE_DRAWER_SIZE}
                duration={200}
                className="payment-cycles__drawer"
                overlayClassName="payment-cycles__drawer-overlay"
            >
                <div className="payment-cycles__panel">
                    <header className="payment-cycles__panel-head">
                        <div>
                            <p className="payment-cycles__panel-kicker">Payment cycles</p>
                            <h2>How payment cycles work</h2>
                        </div>
                        <button
                            type="button"
                            className="payment-cycles__panel-close"
                            onClick={() => setGuideOpen(false)}
                        >
                            Close
                        </button>
                    </header>
                    <div className="payment-cycles__panel-body">
                        <PaymentCyclesGuide />
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default PaymentCycles;
